import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IDeviceContextHandler, IDeviceContext, ISkinInformation, SOTIHTMLCanvasElement, IDeviceContextSkinInfo, IDeviceCanvas } from '../mcrc-api';
import { ColorQuality, IInitResponsePacket, IMonitorInfo, IScreenPacket, ReturnCode, ScreenMessageFlags } from 'app/modules/remote-control-ng/definitions';
import { RcLoggerService } from '../services/rcLoggerService';
import { RcContext } from './rcContext';
import { DeviceContext } from './deviceContext';
import { MathHelpers } from '../utils/math';
import { RestartThrottling } from '../services/restartThrottling';
import { cloneDeep } from 'lodash';

/**
 * TODO:
 *    1. Move all the update***Response update***Packet function related to handle the messages to corresponding commands
 *    2. Try to delete the _deviceContext stuff, all RC instance status should be placed into the RcContext and remove deep copy
 *    3. Try to split/put into different services
 */
export class DeviceContextHandler implements IDeviceContextHandler {
    /**
     * Keeps track of the DeviceContext
     */
    private _deviceContext: IDeviceContext = {} as IDeviceContext;
    // {
    //     device: { // What we know about the real device, updated with initResponse, loginResponse, and every screenPacket received
    //         loaded: false, // Will be true after receiving information about the device (either InitResponse or LoginResponse)
    //         osType: null,
    //         width: 0, // Width is always "the edge closest to the floor" (no matter what the rotation is)
    //         height: 0,
    //         rotation: 0, // Rotation from 0 (device default position) to 3, turning counter clockwise
    //         scale: 10, // The scale the device is sending us images (this info is coming on every screenPacket)
    //         supportsScreenProjection: false,
    //         fullscreenLoaded: false, // True once we received the first full screen message
    //         fullScreenLoaded$: new BehaviorSubject(false),
    //     },
    //     skin: { // Information we have from the skin
    //         loaded: false, // Will be true after receiving the skin information and the skin image
    //         width: 0,
    //         height: 0,
    //         rotation: 0,
    //         image: null, // The skin's image we render
    //         isIrregular: false, // Irregular screens within skins are screens that are not rectangular. If true, skin's screen area is transparent allowing skin to be rendered on top of the device screen image
    //         screen: { // The Screen area within the skin
    //             width: 0,
    //             height: 0,
    //             top: 0,
    //             left: 0,
    //             bottom: 0,
    //             right: 0,
    //         },
    //     },
    //     canvas: {
    //         // cx: 0, cy: 0 are not needed as they are just a copy of skin.screen.top and skin.screen.left
    //         loaded: false,    // Will be true after the canvas is ready to be used
    //         width: 0,     // (replaces ctx.canvas.size.width)
    //         height: 0,    // (replaces ctx.canvas.size.height)
    //         element: null, // RC Canvas (replaces ctx.canvas)
    //         init: undefined
    //     },
    //     client: {
    //         rotation: 0, // The current client rotation
    //         targetRotation: 0, // The target client rotation we want to get at, (used for animation)
    //         scale: 0, // The scale the image needs to be at to fit within the canvas
    //         scaleFactor: 1, // The factor between the Device scale and the client scale
    //         restartInfo: { // Information used to track what was the last restart message configuration we sent
    //             scale: undefined,
    //             bitDepth: undefined,
    //             imageQuality: undefined,
    //         },
    //     },
    // };

    private _contextReadySubject: BehaviorSubject<boolean>;
    contextReady$: Observable<boolean>;

    private _contextReadyForFirstRestartMessageSubject: BehaviorSubject<boolean>;
    contextReadyForFirstRestartMessage$: Observable<boolean>;

    private _rotationChangedSubject: Subject<number>;
    rotationChanged$: Observable<number>;

    private _firstRestartSent: boolean;
    private _isContextReady: boolean;
    private logger: RcLoggerService;
    private restartThrottle: RestartThrottling;

    constructor() {
        this.logger = RcContext.instance.getService(RcLoggerService);
        this.restartThrottle = RcContext.instance.getService(RestartThrottling);

        this._contextReadySubject = new BehaviorSubject(false);
        this.contextReady$ = this._contextReadySubject.asObservable();

        this._contextReadyForFirstRestartMessageSubject = new BehaviorSubject(false);
        this.contextReadyForFirstRestartMessage$ = this._contextReadyForFirstRestartMessageSubject.asObservable();

        this._rotationChangedSubject = new Subject();
        this.rotationChanged$ = this._rotationChangedSubject.asObservable();

        this._firstRestartSent = false;
        this._isContextReady = false;

        this._deviceContext.skin = <IDeviceContextSkinInfo>{
            // Information we have from the skin
            loaded: false,  // Will be true after receiving the skin information and the skin image
            width: 0,
            height: 0,
            rotation: 0,
            image: null, // The skin's image we render
            isIrregular: false, // Irregular screens within skins are screens that are not rectangular. If true, skin's screen area is transparent allowing skin to be rendered on top of the device screen image
            screen: { // The Screen area within the skin
                width: 0,
                height: 0,
                top: 0,
                left: 0,
                bottom: 0,
                right: 0
            }
        };

        this._deviceContext.device = {
            // What we know about the real device, updated with initResponse, loginResponse, and every screenPacket received
            loaded: false,  // Will be true after receiving information about the device (either InitResponse or LoginResponse)
            osType: null,
            width: 0,   // Width is always "the edge closest to the floor" (no matter what the rotation is)
            height: 0,
            rotation: 0, // Rotation from 0 (device default position) to 3, turning counter clockwise
            scale: 10, //The scale the device is sending us images (this info is coming on every screenPacket)
            supportsScreenProjection: false,
            fullscreenLoaded: false, // True once we received the first full screen message
            fullScreenLoaded$: new BehaviorSubject(false)
        };

        this._deviceContext.client = {
            rotation: 0,    // The current client rotation
            targetRotation: 0,  // The target client rotation we want to get at, (used for animation)
            scale: 0, // The scale the image needs to be at to fit within the canvas
            scaleFactor: 1, // The factor between the Device scale and the client scale
            restartInfo: {  // Information used to track what was the last restart message configuration we sent
                scale: undefined,
                bitDepth: undefined,
                imageQuality: undefined
            }
        };

        this._deviceContext.canvas = {
            loaded: false,
            width: 0,
            height: 0,
            element: null
        } as IDeviceCanvas;
    }

    /**
     * Returns a current instance or the deep clone of the deviceContext.
     */
    public currentContext(clone?: boolean): IDeviceContext {
        // Clone the current context return the clone...
        // Clone is done so if someone changes the context returned, the current context is not affected.
        // This is not needed when a simple skin check is done
        return !clone ? this._deviceContext : cloneDeep(this._deviceContext);
    }

    public isContextReady(): boolean {
        return this._isContextReady;
    }

    public transfer(deviceContext: IDeviceContext) {
        // Clone the context received and sets it as the currentContext
        // This is done so if someone changes any property within the old context, the current context is not affected
        // only use case is in the RcContext, which is already cloned the origin deviceContext
        this._deviceContext = cloneDeep(deviceContext);
        this._deviceContext.device.fullscreenLoaded = false;
        this._deviceContext.device.fullScreenLoaded$.next(this._deviceContext.device.fullscreenLoaded);

        this._deviceContext.canvas = {
            loaded: false,
            width: 0,
            height: 0,
            element: null,
            init: undefined,
            clientHeight: undefined,
            clientWidth: undefined
        };

        this._checkIfContextIsReady();
    }

    /**
     * Adds to the deviceContext the relevant information we receive in the InitResponseMessage
     */
    public updateWithInitResponse(initResponse: IInitResponsePacket) {
        this.logger.debug('Updating deviceContext with InitResponse...');

        if (initResponse.returnCode == ReturnCode.PCR_OK) {
            this._deviceContext.device.osType = initResponse.osType;

            // When "Control Without Notification" permission is false, initResponse has NO ScreenInfo
            // (it comes in the loginResponse message)
            if (initResponse.hasScreenInfo && initResponse.width && initResponse.height) {
                this._deviceContext.device.width = initResponse.width;
                this._deviceContext.device.height = initResponse.height;
                this._deviceContext.device.rotation = initResponse.rotation;
                this._deviceContext.device.loaded = true;
            }
        }

        this._checkIfContextIsReady();
    }

    /**
     * Adds to the deviceContext the relevant information we receive in the LoginResponseMessage
     * When "Control Without Notification" permission is false, ScreenInfo comes in loginResponse message
     */
    public updateWithLoginResponse(loginResponse: IInitResponsePacket) {
        this.logger.debug('Updating device context with LoginResponse...');

        if (loginResponse.returnCode == ReturnCode.PCR_OK) {

            this._deviceContext.device.osType = loginResponse.osType;

            // When "Control Without Notification" permission is false, ScreenInfo comes in loginResponse message
            if (loginResponse.hasScreenInfo && loginResponse.width && loginResponse.height) {
                this._deviceContext.device.width = loginResponse.width;
                this._deviceContext.device.height = loginResponse.height;
                this._deviceContext.device.rotation = loginResponse.rotation;
                this._deviceContext.device.loaded = true;
            }
        }

        this._checkIfContextIsReady();
    }

    /**
     * Updates the context with the loaded Skin Information
     */
    public updateWithSkinInformation(skinInformation: ISkinInformation, skinImage: HTMLImageElement) {
        this.logger.debug('Updating device context with skin info: ' + JSON.stringify(skinInformation) + ' image:' + (skinImage ? 'valid' : 'null'));
        // If we were able to load a skin for this device..
        if (skinInformation && skinImage) {
            this._deviceContext.skin.width = skinInformation.deviceWidth; // This is the width of the image of the device
            this._deviceContext.skin.height = skinInformation.deviceHeight;
            this._deviceContext.skin.rotation = skinInformation.rotation;
            this._deviceContext.skin.isIrregular = skinInformation.hasNotchImage ? true : false;
            this._deviceContext.skin.screen.top = skinInformation.screen.top;
            this._deviceContext.skin.screen.left = skinInformation.screen.left;
            this._deviceContext.skin.screen.bottom = skinInformation.screen.bottom;
            this._deviceContext.skin.screen.right = skinInformation.screen.right;

            this._deviceContext.skin.screen.width = this._deviceContext.skin.screen.right - this._deviceContext.skin.screen.left;
            this._deviceContext.skin.screen.height = this._deviceContext.skin.screen.bottom - this._deviceContext.skin.screen.top;

            // SkinInformation dimensions must be the same as the skin image dimensions
            if (this._deviceContext.skin.width !== skinImage.width) {
                console.error('Skin information mismatches skin image. Info.width=', this._deviceContext.skin.width, ' image.width=', skinImage.width)
            }
            if (this._deviceContext.skin.height !== skinImage.height) {
                console.error('Skin information mismatches skin image. Info.height=', this._deviceContext.skin.height, ' image.height=', skinImage.height)
            }

            this._deviceContext.skin.image = skinImage;

            // Legacy deviceContext information
            const ctx = RcContext.instance.deviceContext as DeviceContext;
            if (ctx) {
                ctx.skinScreenWidth = this._deviceContext.skin.screen.width;
                ctx.skinScreenHeight = this._deviceContext.skin.screen.height
                ctx.deviceWidth = this._deviceContext.skin.width
                ctx.deviceHeight = this._deviceContext.skin.height;
            }
        } else {
            // There is no skin available, configure the context accordingly
            // If the device is rotated, we need to rotate the empty image accordingly, so the un-rotated measures are correct
            if (this._deviceContext.device.rotation === 0 || this._deviceContext.device.rotation === 2) {

                this._deviceContext.skin.width = this._deviceContext.device.width;
                this._deviceContext.skin.screen.width = this._deviceContext.device.width;

                this._deviceContext.skin.height = this._deviceContext.device.height;
                this._deviceContext.skin.screen.height = this._deviceContext.device.height;
            } else {
                // as the device is in rotation 1 or 3, we need to switch width with height, so the dimensions match a rotation 0
                this._deviceContext.skin.width = this._deviceContext.device.height;
                this._deviceContext.skin.screen.width = this._deviceContext.device.height;

                this._deviceContext.skin.height = this._deviceContext.device.width;
                this._deviceContext.skin.screen.height = this._deviceContext.device.width;
            }

            this._deviceContext.skin.rotation = 0;

            this._deviceContext.skin.screen.top = 0;
            this._deviceContext.skin.screen.left = 0;
            this._deviceContext.skin.screen.bottom = 0;
            this._deviceContext.skin.screen.right = 0;
        }

        this._deviceContext.skin.loaded = true;    // Even dough there is no real skin, we mark it loaded

        this._checkIfContextIsReady();

    }

    /**
     * Updates the main RC canvas information with the current state of the #rc-canvas canvas
     */
    public updateWithCanvasChanges(width: number, height: number) {
        if (width > 0 && height > 0) {
            if (!this._deviceContext.canvas.element) {
                this._deviceContext.canvas.element = $('#rc_canvas').get(0) as SOTIHTMLCanvasElement;
            }

            this._deviceContext.canvas.width = width;
            this._deviceContext.canvas.height = height;
            this._deviceContext.canvas.loaded = true;

            if (width !== this._deviceContext.canvas.width || height !== this._deviceContext.canvas.height) {
                this.logger.debug('Updating device context with canvas change: width:' + width + ' height:' + height);
            }
        } else {
            this._deviceContext.canvas.loaded = false;
        }

        this._checkIfContextIsReady();
    }

    /**
     * Updates the deviceContext with the information we receive in a ScreenPacket
     */
    public updateWithScreenPacket(screenPacket: IScreenPacket) {
        if (screenPacket.scale !== this._deviceContext.device.scale || screenPacket.rotation !== this._deviceContext.device.rotation) {
            this.logger.debug('Updating device context with screenPacket: fullscreen:' + screenPacket.fullscreen + ' scale:' + screenPacket.scale + ' rotation:' + screenPacket.rotation);
        }

        this._deviceContext.device.scale = screenPacket.scale;

        // As the context.device information is about the real device, if a screen packet says the device
        // went from portrait to landscape and vice versa, we need to switch the width and height.
        // The device's width should ALWAYS be the horizontal side of it.
        if ((MathHelpers.isEven(this._deviceContext.device.rotation) && MathHelpers.isOdd(screenPacket.rotation))
            || (MathHelpers.isOdd(this._deviceContext.device.rotation) && MathHelpers.isEven(screenPacket.rotation))) {
            const width = this._deviceContext.device.width;
            this._deviceContext.device.width = this._deviceContext.device.height;
            this._deviceContext.device.height = width;
        }

        if (this._deviceContext.device.rotation !== screenPacket.rotation) {
            this._deviceContext.device.rotation = screenPacket.rotation;
            this._rotationChangedSubject.next(this._deviceContext.device.rotation);
        }

        if (screenPacket.fullscreen) {
            this._deviceContext.device.supportsScreenProjection = (screenPacket.imageDataFlags & ScreenMessageFlags.DeviceProjection) != 0;

            this._deviceContext.device.fullscreenLoaded = true;
            this._deviceContext.device.fullScreenLoaded$.next(true);
        }

        this._checkIfContextIsReady();
    }

    /**
     * Updates the context with changes on the Client Side rotation
     * (real client side rotation or target client side rotation)
     */
    public updateWithClientRotation(clientRotation: number, clientTargetRotation: number) {
        this.logger.debug('Updating device context with client rotation: targetRotation:' + clientTargetRotation + ' rotation:' + clientRotation);

        if (clientTargetRotation >= 0 && clientTargetRotation <= 3) {
            this._deviceContext.client.targetRotation = clientTargetRotation;
        }

        if (clientRotation >= 0 && clientRotation <= 3) {
            this._deviceContext.client.rotation = clientRotation;
        }

    }

    /**
     * Called to keep track of parameters of the last restart message we sent to the agent
     */
    public updateWithRestartInfo(scale: number, bitDepth: ColorQuality, imageQuality: number) {
        this.logger.debug('Updating device context with restart info. scale:' + scale + ' bitDepth:' + bitDepth + ' imageQuality:' + imageQuality);
        if (scale) {
            this._deviceContext.client.restartInfo.scale = scale;
        }

        this._deviceContext.client.restartInfo.bitDepth = bitDepth;
        this._deviceContext.client.restartInfo.imageQuality = imageQuality;

    }

    /**
     * Called when the RC session finishes, to be able to do some cleanup when this happens
     */
    public updateWithSessionEnd() {
        this.logger.debug('Updating context with RC session end');
        this._deviceContext.device.loaded = false;
        this._deviceContext.device.rotation = 0;
        this._deviceContext.client.restartInfo.scale = undefined;
        this._deviceContext.client.restartInfo.bitDepth = undefined
        this._deviceContext.client.restartInfo.imageQuality = undefined;
        this._deviceContext.skin.loaded = false;
        this._firstRestartSent = false;

        this._checkIfContextIsReady();
    }

    public updateWithClientScale(clientScale: number) {
        this._deviceContext.client.scale = clientScale;

        this._deviceContext.client.scaleFactor = this._deviceContext.client.scale / (this._deviceContext.device.scale / 100);

        if (this._deviceContext.device.scale < 100 && this._deviceContext.device.scale / 100 < this._deviceContext.client.scale) {
            // The device scale we have is smaller than what we are showing on screen (we are enlarging it on client side)
            // And the device is not at 1005 yet, so we cn get a better image.
            // client.scale value is between 0 and 1
            this.restartThrottle.send(this._deviceContext.client.scale * 100, false, RcContext.instance);
        }

        this.logger.debug('Scales: device: ' + this._deviceContext.device.scale
            + ' client: ' + this._deviceContext.client.scale
            + ' scaleFactor:' + this._deviceContext.client.scaleFactor);

    }

    public updateWithNewMonitor(monitor: IMonitorInfo) {
        this.logger.debug('Updating context with new monitor: ' + JSON.stringify(monitor, (key, value) => {
            if (key === "rotation$") return undefined;
            else return value;
        }));
        this._deviceContext.device.width = monitor.width;
        this._deviceContext.device.height = monitor.height;
        this._deviceContext.device.rotation = monitor.rotation || 0;
        this._deviceContext.device.loaded = true;

        if (this._deviceContext.skin) {
            this._deviceContext.skin.rotation = this._deviceContext.device.rotation;
        }
    }

    private _checkIfContextIsReady() {
        // Check if we have all we need to send the first restart message
        const contextReadyForFirstRestartMessage = this._deviceContext.device.loaded
            && this._deviceContext.skin.loaded
            && this._deviceContext.canvas.loaded;


        // The device context handler seems to be the only place that has enough information to
        // know when the first restart message can be sent.
        if (contextReadyForFirstRestartMessage && !this._firstRestartSent) {
            this.logger.debug('contextReadyForFirstRestartMessage');
            const transformService = RcContext.instance.deviceScreenTransformService;
            if (transformService) {
                if (transformService.isAutoZoomActive()) {
                    transformService.autoZoomToCurrentViewSize(true);
                    this.restartThrottle.send(transformService.getScreenScale(), true, RcContext.instance);
                }
                this._firstRestartSent = true;
                this._contextReadyForFirstRestartMessageSubject.next(contextReadyForFirstRestartMessage);
            } else {
                this.logger.error('DeviceContextHandler._checkIfContextIsReady: transformService not ready in first restart check');
            }
        }

        const contextReady = this._deviceContext.device.loaded
            && this._deviceContext.skin.loaded
            && this._deviceContext.device.fullscreenLoaded
            && this._deviceContext.canvas.loaded;

        if (contextReady !== this._isContextReady) {
            // If the readiness of the context changed, then let anyone interested know about it
            // Fire an event for any one waiting for the context to be ready
            this._isContextReady = contextReady;
            this._contextReadySubject.next(contextReady);
            if (this._isContextReady) {
                this.logger.debug('ContextReady');
                const transformService = RcContext.instance.deviceScreenTransformService;
                if (transformService) {
                    if (transformService.isAutoZoomActive()) {
                        transformService.autoZoomToCurrentViewSize();
                    }
                    transformService.makeCanvasVisible();
                } else {
                    this.logger.error('DeviceContextHandler._checkIfContextIsReady: transformService not ready for context change');
                }
            }
        }
    }
}
